import React from 'react';
import Image from 'next/image';
import round from 'lodash/round';
import upperFirst from 'lodash/upperFirst';
import Tooltip from '@mui/material/Tooltip';
import { useClintMediaQuery } from '@/utils/hooks';
import ChartBgImage from '@/assets/result-chart/chart.svg';

export const DEFAULT_CHART = [
  {
    bg_n: 'linear-gradient(0deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.3)), linear-gradient(163.26deg, rgba(255, 255, 255, 0.8) -16.11%, rgba(255, 255, 255, 0.4) 88.44%), linear-gradient(180deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0) 100%), #4ADE80',
    bg_p: '#4ade80',
    dir_Y: 'top',
    dir_X: 'right',
    text: { en: 'A', zn: '创新' },
    toolTip: {
      name: 'Adhocracy A型团队 — 创造',
      feather: '强调独立性与专业性',
      en: 'Do things first',
      briefDesc:
        'A型人才：拥有极高的专业能力，对工作的独立性与灵活性有较高要求',
    },
  },
  {
    bg_n: 'linear-gradient(0deg, rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.7)), linear-gradient(163.26deg, rgba(255, 255, 255, 0.8) -16.11%, rgba(255, 255, 255, 0.4) 88.44%), linear-gradient(68.81deg, rgba(0, 0, 0, 0.2) -87.78%, rgba(0, 0, 0, 0) 77.91%), #FACC15',
    bg_p: '#facc15',
    dir_Y: 'top',
    dir_X: 'left',
    text: { en: 'C', zn: '协作' },
    toolTip: {
      name: 'Clan C型团队 — 协作',
      feather: '以协作为主',
      en: 'Do things together',
      briefDesc:
        'C型人才：对工作团队有强烈的归属感，期待拥有稳定的工作及人际关系',
    },
  },
  {
    bg_n: 'linear-gradient(0deg, rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.7)), linear-gradient(163.26deg, rgba(255, 255, 255, 0.8) -16.11%, rgba(255, 255, 255, 0.4) 88.44%), #F87171',
    bg_p: '#f87171',
    dir_Y: 'bottom',
    dir_X: 'left',
    text: { en: 'H', zn: '规则' },
    toolTip: {
      name: 'Hierarchy H型团队 — 规则',
      feather: '强调按照规则做事',
      en: 'Do things right',
      briefDesc:
        'H型人才：拥有严谨且细致的工作态度，对自己的工作目标有清晰的管理与定位',
    },
  },
  {
    bg_n: 'linear-gradient(0deg, rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.7)), linear-gradient(163.26deg, rgba(255, 255, 255, 0.8) -16.11%, rgba(255, 255, 255, 0.4) 88.44%), #60A5FA',
    bg_p: '#60a5fa',
    dir_Y: 'bottom',
    dir_X: 'right',
    text: { en: 'M', zn: '竞争' },
    toolTip: {
      name: 'Market M型团队 — 竞争',
      feather: '依靠竞争来获得收益',
      en: 'Do things fast',
      briefDesc: 'M型人才：高情商与高抗压能力并存，信奉多劳多得的理念',
    },
  },
];

const ResultChart = (props: {
  teamTypePreference: string;
  chartSize?: number;
  posChartNeedShadow?: boolean;
}) => {
  const defaultChartSize = 600;
  const {
    teamTypePreference,
    chartSize = 600,
    posChartNeedShadow = true,
  } = props;
  const positiveChartContent = DEFAULT_CHART.find(
    (item) => item.text.en === teamTypePreference[0],
  );
  const halfSize = round(chartSize * 0.5);
  const negChartSize = round(chartSize * 0.333);
  const posChartSize = round(chartSize * 0.383);
  const negDirectionPos = round(halfSize * 0.293);
  const posDirectionPos = round(halfSize * 0.193);
  const negTextPosX = round(negChartSize * 0.47);
  const negTextPosY = round(negChartSize * 0.52);
  const posTextSize = round(chartSize / defaultChartSize, 1);
  const posTextPosX = round(posChartSize * 0.565);
  const posTextPosY = round(posChartSize * 0.495);
  const posToolTipTextPosX = round(posTextPosX * 0.789);
  const posToolTipTextPosY = round(posChartSize - posTextPosY - 44);

  const isMobile = useClintMediaQuery();

  const MyToolTip = (props: {
    title: React.ReactNode;
    placement: string;
    children: React.ReactElement<any, any>;
  }) => {
    const { title, children, placement } = props;
    const [tooltipOpen, setToolTipOpen] = React.useState(false);

    React.useEffect(() => {
      const removeToolTip = () => {
        !isMobile && tooltipOpen && setToolTipOpen(false);
      };
      window.addEventListener('scroll', removeToolTip);
      return () => window.removeEventListener('scroll', removeToolTip);
    }, [tooltipOpen]);

    return (
      <Tooltip
        title={title}
        disableTouchListener={true}
        followCursor={true}
        placement={placement as 'left' | 'right'}
        PopperProps={{
          disablePortal: true,
          sx: {
            '.MuiTooltip-tooltip': {
              background: '#fff',
              boxShadow: '0px 10px 20px rgba(0, 0, 0, 0.05)',
              borderRadius: '8px',
              padding: '24px 20px',
              margin: 0,
              display: tooltipOpen ? 'block' : 'none',
            },
          },
        }}
      >
        <div
          onMouseMove={() => !isMobile && !tooltipOpen && setToolTipOpen(true)}
        >
          {children}
        </div>
      </Tooltip>
    );
  };

  return (
    <div
      className="relative flex-shrink-0 mb-4"
      style={{
        width: chartSize,
        height: chartSize,
      }}
    >
      <Image
        src={ChartBgImage}
        alt=""
        priority
        fill
        style={{
          objectFit: 'contain',
        }}
      />
      {DEFAULT_CHART.map((item, index) => (
        <div
          key={item.bg_n + index}
          className="absolute"
          style={{
            [item.dir_Y]: negDirectionPos,
            [item.dir_X]: negDirectionPos,
          }}
        >
          <MyToolTip
            title={
              <div className="text-black">
                <p className="text-xl font-semibold mb-2.5 whitespace-nowrap">
                  {item.toolTip.name}
                </p>
                <p className="text-base font-semibold mb-2.5">
                  {item.toolTip.feather}
                </p>
                <p className="text-sm leading-5.5">{item.toolTip.en}</p>
              </div>
            }
            placement={item.dir_X}
          >
            <div
              className="overflow-hidden relative cursor-pointer"
              style={{
                width: negChartSize,
                height: negChartSize,
                borderRadius: '4px 4px 4px 4px',
                [`border${upperFirst(item.dir_Y)}${upperFirst(
                  item.dir_X,
                )}Radius`]: `${negChartSize}px`,
                background: item.bg_n,
              }}
            >
              <p
                className="text-base text-black absolute"
                style={{
                  [item.dir_Y]: negTextPosY,
                  [item.dir_X]: negTextPosX,
                }}
              >
                <span className="inline-block mr-1 font-semibold opacity-40">
                  {item.text.en}
                </span>
                <span className="inline-block text-slate-500">
                  {item.text.zn}
                </span>
              </p>
            </div>
          </MyToolTip>
        </div>
      ))}
      {positiveChartContent && (
        <div
          className="absolute"
          style={{
            [positiveChartContent.dir_Y]: posDirectionPos,
            [positiveChartContent.dir_X]: posDirectionPos,
          }}
        >
          <div
            className="overflow-hidden relative rounded"
            style={{
              width: posChartSize,
              height: posChartSize,
              [`border${upperFirst(positiveChartContent.dir_Y)}${upperFirst(
                positiveChartContent.dir_X,
              )}Radius`]: `${posChartSize}px`,
              background: positiveChartContent.bg_p,
            }}
          >
            <p
              className="text-black absolute font-semibold"
              style={{
                [positiveChartContent.dir_Y]: posTextPosY,
                [positiveChartContent.dir_X]: posTextPosX,
              }}
            >
              <span className="inline-block mr-1.5 font-semibold leading-14 text-[2.375rem]">
                {positiveChartContent.text.en}
              </span>
              <span className="inline-block text-base text-slate-800">
                {positiveChartContent.text.zn}
              </span>
            </p>
          </div>
          <div
            className={`text-black absolute px-5 py-6 rounded-lg bg-white${posChartNeedShadow ? ' shadow-[0px_10px_20px_rgba(0,0,0,0.05)]' : ''}`}
            style={{
              [positiveChartContent.dir_Y === 'top' ? 'bottom' : 'top']:
                posToolTipTextPosY,
              [positiveChartContent.dir_X === 'left' ? 'right' : 'left']:
                posToolTipTextPosX,
              scale: `${posTextSize}`,
              transformOrigin: `${
                positiveChartContent.dir_X === 'left' ? 'right' : 'left'
              } ${positiveChartContent.dir_Y === 'top' ? 'bottom' : 'top'}`,
            }}
          >
            <p className="text-xl font-semibold whitespace-nowrap mb-2.5">
              {positiveChartContent.toolTip.name}
            </p>
            <p className="text-base font-semibold mb-2.5">
              {positiveChartContent.toolTip.feather}
            </p>
            <p className="text-sm leading-5.5">
              {positiveChartContent.toolTip.en}
            </p>
            <p className="text-sm leading-5.5 mt-6">
              {positiveChartContent.toolTip.briefDesc}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
export default ResultChart;
